import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { generateRandomString } from "utils/helper";
import * as services from "../services/leaveTypeApi";
import { setIsLoading, setLeaveTypeList, setLeaveCategoryOption, setLeaveTypeById } from "../slices/attendanceSlice";
import useOptions from "features/masterData/hooks/useOptions";
import { useTranslation } from "react-i18next";
import { message, Modal } from "antd";

function toPaginationParams(params = {}) {
  const { pagination = {}, sortBy = [] } = params;
  const modifiedParams = {
    ...params,
    page: pagination.page || 0,
    size: pagination.pageSize || 10,
    sortBy: sortBy
      .filter((s) => s.order !== undefined)
      ?.map((s) => ({
        direction: s.order === "descend" ? "desc" : "asc",
        property: s.field,
      })),
  };
  return modifiedParams;
}

function useLeaveType() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation()
  const currentCompany = useSelector((state) => state.company.currentCompany);
  const currentEmployee = useSelector((state) => state.company.currentEmployee);
  const isLoading = useSelector((state) => state.attendance.isLoading);
  const leaveTypeList = useSelector((state) => state.attendance.leaveTypeList);
  const leaveCategoryOption = useSelector((state) => state.attendance.leaveCategoryOption);
  const [pagination, setPagination] = useState({ page: 1, size: 10 });
  const [sorter, setSorter] = useState([]);

  // const {
  //   leaveGroupOptions,
  //   getLeaveGroupOptions,
  // } = useOptions();
  const modifiedLeaveGroupOptions = useMemo(() => {
    const modifiedOptions = leaveCategoryOption?.map((opt) => ({
      ...opt,
      label: i18n.language === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [i18n.language, leaveCategoryOption]);

  const getLeaveCategoryOption = useCallback(async () => {
    try {
      dispatch(setIsLoading(true));
      const response = await services.getLeaveCategoryOption({
        requestId: generateRandomString(),
        companyId: currentCompany,
        employeeId: currentEmployee,
      });
      dispatch(setLeaveCategoryOption(response?.data))
    } catch (error) {
      message.error(t("common.error"))
    } finally {
      dispatch(setIsLoading(false));
    }
  })

  const getLeaveTypeCompany = useCallback(
    async (params = {}) => {
      try {
        dispatch(setIsLoading(true));
        const response = await services.getLeaveTypeCompany({
          ...params,
          requestId: generateRandomString(),
          companyId: currentCompany,
          employeeId: currentEmployee,
        });
        dispatch(
          setLeaveTypeList(response.data?.content || [])
        );

        setPagination({
          page: params.page + 1,
          size: params.size,
          range: [
            params.page * params.size + 1,
            Math.min((params.page + 1) * params.size, response.data?.totalItems),
          ],
          total: response.data?.totalItems,
        });
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, pagination, dispatch]
  );

  const onCreate = useCallback(() => {
    navigate("/configuration/attendance/leave_type/create");
  }, [navigate]);

  const onStatus = useCallback(
    async (val, record) => {
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (i18n.language === "en"
          ? <span>Are you sure confirm to change status ?</span>
          : <span>คุณยืนยันการเปลี่ยนสถานะใช่หรือไม่ ?</span>
        ),
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
        onOk: async () => {
          try {
            // dispatch(setIsLoading(true));
            const statusFlag = val ? "ACTIVE" : "INACTIVE";
            const values = {
              stpLeaveTypeId: record.id,
              requestId: generateRandomString(),
              companyId: currentCompany,
              employeeId: currentEmployee,
              statusFlag,
            };
            await services.manageStatusLeaveTypeCompany(values);
            message.success(t("common.success"))
            // const modifiedList = leaveTypeList.map((s) => {
            //   if (s.id === record.id) {
            //     return { ...s, statusFlag };
            //   }
            //   return s;
            // });
            getLeaveTypeCompany({
              page: pagination.page - 1,
              size: pagination.size,
            });
            // dispatch(setLeaveTypeList(modifiedList));
          } catch (error) {
            message.error(t("common.error"))
          } finally {
            // dispatch(setIsLoading(false));
          }
        }
      })
    },
    [leaveTypeList, currentCompany, getLeaveTypeCompany, dispatch]
  );

  const getLeaveTypeById = useCallback(
    async (leaveTypeCompanyId) => {
      try {
        dispatch(setIsLoading(true));
        const response = await services.getLeaveType({
          leaveTypeCompanyId
        });
        dispatch(
          setLeaveTypeById(response.data || [])
        );
      } catch (error) {
        // message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [dispatch]
  );


  const onEdit = useCallback(
    (record) => {
      if (record.id) {
        // navigate(`/configuration/attendance/leave_type/${record.id}/edit`);
        navigate(`/configuration/attendance/leave_type/${record.id}/${record.isView ? 'view' : 'edit'}`, { state: { ...record } })
        getLeaveTypeById(record?.id);
      }
    },
    [navigate]
  );

  const onSearch = useCallback(
    (values) => {

      values.page = 0;
      values.size = 50;
      getLeaveTypeCompany(values)
    },
    []
  );

  const onDelete = useCallback(
    async (record) => {
      try {
        dispatch(setIsLoading(true));
        const values = {
          stpLeaveTypeId: record.id,
          requestId: generateRandomString(),
          companyId: currentCompany,
          employeeId: currentEmployee,
          statusFlag: "DELETE",
        };
        await services.manageStatusLeaveTypeCompany(values);
        message.success(t("common.success"))
        // const modifiedShiftsList = leaveTypeList.map((s) => s.id !== record.id);
        getLeaveTypeCompany({
          page: pagination.page - 1,
          size: pagination.size,
        });
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [leaveTypeList, currentCompany, getLeaveTypeCompany, dispatch]
  );

  const onChange = useCallback(() => { }, []);

  const handleOnChange = useCallback(
    (tablePagination, _, tableSorter) => {
      setPagination(tablePagination);
      const modifiedSorter = Array.isArray(tableSorter)
        ? [...tableSorter]
        : [tableSorter];
      setSorter(modifiedSorter);
      const modifiedParams = toPaginationParams({
        pagination: {
          page: tablePagination.current - 1,
          pageSize: tablePagination.pageSize,
        },
        sortBy: modifiedSorter,
      });
      // TODO: If sorting or filtering has changed, Change page to 0
      getLeaveTypeCompany(modifiedParams);
    },
    [getLeaveTypeCompany]
  );

  useEffect(() => {
    if (typeof currentCompany !== "object") {
      getLeaveTypeCompany({
        page: 0,
        size: 10,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany]);

  useEffect(() => {
    getLeaveCategoryOption();
  }, []);

  return {
    isLoading,
    pagination,
    leaveTypeList,
    leaveGroupOptions: modifiedLeaveGroupOptions,
    onCreate,
    onStatus,
    onEdit,
    onSearch,
    onDelete,
    onChange: handleOnChange,
    getLeaveCategoryOption,
    getLeaveTypeById
  };
}

export default useLeaveType;
